import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { InstantShiftFillCard } from './InstantShiftFillCard';
import { Briefcase, Calendar, DollarSign, MapPin, MessageSquare, Users, Edit3, Trash2, XCircle, ChevronDown, ChevronUp, RotateCcw, UserPlus, RefreshCw } from 'lucide-react';

const toDateInputValue = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().split('T')[0];
};

export const OwnerDashboardManager = ({ ownerId }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [expandedJobId, setExpandedJobId] = useState(null);
  const [actionLoadingKey, setActionLoadingKey] = useState('');
  const [applicantFilterByJob, setApplicantFilterByJob] = useState({});

  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [editForm, setEditForm] = useState({
    title: '',
    hourly_rate: '',
    start_date: '',
    description: '',
  });

  const fetchOwnerManagementData = useCallback(async ({ showLoader = true } = {}) => {
    try {
      if (showLoader) {
        setLoading(true);
      } else {
        setIsRefreshing(true);
      }

      const [jobsResponse, applicationsResponse] = await Promise.all([
        axios.get('/jobs/owner/managed?limit=100&include_closed=true'),
        axios.get('/applications/my'),
      ]);

      const ownerJobs = jobsResponse.data || [];
      setJobs(ownerJobs);
      setApplications(applicationsResponse.data || []);
    } catch (error) {
      console.error('Failed to fetch owner dashboard management data:', error);
      toast.error('Failed to load owner management data');
    } finally {
      if (showLoader) {
        setLoading(false);
      } else {
        setIsRefreshing(false);
      }
    }
  }, []);

  useEffect(() => {
    if (ownerId) {
      fetchOwnerManagementData();
    }
  }, [ownerId, fetchOwnerManagementData]);

  const applicationsByJob = useMemo(() => {
    return applications.reduce((acc, application) => {
      const current = acc[application.job_id] || [];
      acc[application.job_id] = [...current, application];
      return acc;
    }, {});
  }, [applications]);

  const handleOpenEdit = (job) => {
    setEditingJob(job);
    setEditForm({
      title: job.title || '',
      hourly_rate: job.hourly_rate || '',
      start_date: toDateInputValue(job.start_date),
      description: job.description || '',
    });
    setShowEditDialog(true);
  };

  const handleUpdateJob = async () => {
    if (!editingJob) return;

    setActionLoadingKey(`edit-${editingJob.id}`);
    try {
      await axios.put(`/jobs/${editingJob.id}`, {
        title: editForm.title,
        hourly_rate: Number(editForm.hourly_rate),
        start_date: editForm.start_date,
        description: editForm.description,
      });

      toast.success('Job updated successfully');
      setShowEditDialog(false);
      setEditingJob(null);
      await fetchOwnerManagementData({ showLoader: false });
    } catch (error) {
      console.error('Failed to update job:', error);
      toast.error(error.response?.data?.detail || 'Failed to update job');
    } finally {
      setActionLoadingKey('');
    }
  };

  const handleCloseJob = async (jobId) => {
    setActionLoadingKey(`close-${jobId}`);
    try {
      await axios.put(`/jobs/${jobId}/close`);
      toast.success('Job closed');
      await fetchOwnerManagementData({ showLoader: false });
    } catch (error) {
      console.error('Failed to close job:', error);
      toast.error(error.response?.data?.detail || 'Failed to close job');
    } finally {
      setActionLoadingKey('');
    }
  };

  const handleReopenJob = async (jobId) => {
    setActionLoadingKey(`reopen-${jobId}`);
    try {
      await axios.put(`/jobs/${jobId}/reopen`);
      toast.success('Job reopened');
      await fetchOwnerManagementData({ showLoader: false });
    } catch (error) {
      console.error('Failed to reopen job:', error);
      toast.error(error.response?.data?.detail || 'Failed to reopen job');
    } finally {
      setActionLoadingKey('');
    }
  };

  const handleInviteBartenders = async (jobId) => {
    setActionLoadingKey(`invite-${jobId}`);
    try {
      const response = await axios.post(`/jobs/${jobId}/invite-bartenders`, {
        max_invites: 10,
        radius_miles: 25,
      });

      const invitedNow = response.data?.invited_now || 0;
      const totalInvited = response.data?.total_invited || 0;
      if (invitedNow > 0) {
        toast.success(`Invited ${invitedNow} nearby bartenders (${totalInvited} total invited)`);
      } else {
        toast.info('No nearby bartenders available to invite right now');
      }

      await fetchOwnerManagementData({ showLoader: false });
    } catch (error) {
      console.error('Failed to invite bartenders:', error);
      toast.error(error.response?.data?.detail || 'Failed to invite bartenders');
    } finally {
      setActionLoadingKey('');
    }
  };

  const handleDeleteJob = async (jobId) => {
    const confirmed = window.confirm('Delete this job and its applications? This action cannot be undone.');
    if (!confirmed) return;

    setActionLoadingKey(`delete-${jobId}`);
    try {
      await axios.delete(`/jobs/${jobId}`);
      toast.success('Job deleted');
      await fetchOwnerManagementData({ showLoader: false });
    } catch (error) {
      console.error('Failed to delete job:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete job');
    } finally {
      setActionLoadingKey('');
    }
  };

  const handleApplicationStatus = async (applicationId, status) => {
    setActionLoadingKey(`${status}-${applicationId}`);
    try {
      await axios.put(`/applications/${applicationId}/status`, { status });
      toast.success(`Application ${status}`);
      await fetchOwnerManagementData({ showLoader: false });
    } catch (error) {
      console.error(`Failed to ${status} application:`, error);
      toast.error(error.response?.data?.detail || `Failed to ${status} application`);
    } finally {
      setActionLoadingKey('');
    }
  };

  const handleMessageApplicant = async (application) => {
    const bartenderName = `${application?.bartender?.first_name || 'Bartender'} ${application?.bartender?.last_name || ''}`.trim();
    const roleTitle = application?.job?.title || 'the role';

    setActionLoadingKey(`message-${application.id}`);
    try {
      await axios.post('/messages', {
        receiver_id: application.bartender_id,
        content: `Hi ${bartenderName.split(' ')[0]}, thanks for applying to ${roleTitle}. We'd like to connect with you about next steps.`,
      });

      toast.success('Message sent to applicant');
      navigate(`/messages?user=${application.bartender_id}`);
    } catch (error) {
      console.error('Failed to message applicant:', error);
      toast.error(error.response?.data?.detail || 'Failed to message applicant');
    } finally {
      setActionLoadingKey('');
    }
  };

  const canUndoClose = (job) => {
    if (!job || job.is_active) return false;
    if (!job.closed_at) return false;

    const closedAt = new Date(job.closed_at);
    if (Number.isNaN(closedAt.getTime())) return false;

    const diffMs = Date.now() - closedAt.getTime();
    return diffMs <= 24 * 60 * 60 * 1000;
  };

  const getApplicantFilter = (jobId) => applicantFilterByJob[jobId] || 'pending';

  const setApplicantFilter = (jobId, status) => {
    setApplicantFilterByJob((prev) => ({
      ...prev,
      [jobId]: status,
    }));
  };

  return (
    <>
      <InstantShiftFillCard onShiftCreated={() => fetchOwnerManagementData({ showLoader: false })} />

      <Card className="bg-white/5 backdrop-blur-md border border-white/5 mb-8" data-testid="owner-management-card">
        <CardHeader className="space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <CardTitle className="font-['Barlow_Condensed'] uppercase flex items-center" data-testid="owner-management-title">
              <Briefcase className="h-5 w-5 mr-2 text-primary" />
              Owner Dashboard Management
            </CardTitle>

            <div className="flex items-center gap-2" data-testid="owner-management-header-actions">
              {isRefreshing && (
                <Badge variant="outline" className="text-xs" data-testid="owner-management-refreshing-indicator">
                  Syncing...
                </Badge>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchOwnerManagementData({ showLoader: false })}
                disabled={isRefreshing || actionLoadingKey !== ''}
                data-testid="owner-management-refresh-btn"
              >
                <RefreshCw className="h-3 w-3 mr-1" />Refresh
              </Button>
            </div>
          </div>

          <CardDescription data-testid="owner-management-description">
            Manage your jobs and applicants directly from your dashboard.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3" data-testid="owner-management-loading-state">
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="rounded-lg border border-white/10 p-4" data-testid={`owner-management-loading-row-${index + 1}`}>
                  <div className="h-5 w-48 rounded bg-white/10 animate-pulse mb-3" />
                  <div className="h-4 w-full rounded bg-white/10 animate-pulse mb-2" />
                  <div className="h-4 w-2/3 rounded bg-white/10 animate-pulse" />
                </div>
              ))}
            </div>
          ) : jobs.length === 0 ? (
            <div className="rounded-lg border border-white/10 p-6 text-center" data-testid="owner-management-empty-state">
              <p className="text-muted-foreground mb-4">No posted shifts yet. Use Instant Shift Fill above, or create a full listing from Jobs.</p>
              <div className="flex flex-col sm:flex-row gap-2 justify-center">
                <Button asChild variant="outline" data-testid="owner-management-account-settings-btn">
                  <Link to="/account-settings">Account Settings</Link>
                </Button>
                <Button asChild variant="outline" data-testid="owner-management-saved-bartenders-btn">
                  <Link to="/saved-bartenders">Saved Bartenders</Link>
                </Button>
                <Button asChild data-testid="owner-management-create-job-btn">
                  <Link to="/jobs">Create Full Job Listing</Link>
                </Button>
                <Button asChild variant="outline" data-testid="owner-management-browse-bartenders-btn">
                  <Link to="/owner/browse-bartenders">Browse Bartenders</Link>
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4" data-testid="owner-managed-jobs-list">
              {jobs.map((job) => {
                const jobApplications = applicationsByJob[job.id] || [];
                const isExpanded = expandedJobId === job.id;
                const isActive = job.is_active !== false;
                const selectedApplicantFilter = getApplicantFilter(job.id);
                const filteredApplications = jobApplications.filter((application) => application.status === selectedApplicantFilter);

                return (
                  <div key={job.id} className="rounded-lg border border-white/10 p-4" data-testid={`owner-job-row-${job.id}`}>
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                      <div>
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <h3 className="text-lg font-semibold text-white" data-testid={`owner-job-title-${job.id}`}>{job.title}</h3>
                          <Badge className={isActive ? 'bg-green-500/20 text-green-400 border-green-500/30' : 'bg-red-500/20 text-red-400 border-red-500/30'} data-testid={`owner-job-status-badge-${job.id}`}>
                            {isActive ? 'Active' : 'Closed'}
                          </Badge>
                          <Badge variant="outline" data-testid={`owner-job-app-count-${job.id}`}>{jobApplications.length} Applicants</Badge>
                          <Badge variant="outline" data-testid={`owner-job-invited-count-${job.id}`}>{job.invited_count || 0} Invited</Badge>
                        </div>
                        <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                          <span className="flex items-center" data-testid={`owner-job-location-${job.id}`}><MapPin className="h-3 w-3 mr-1" />{job.location?.city || 'TBD'}</span>
                          <span className="flex items-center" data-testid={`owner-job-rate-${job.id}`}><DollarSign className="h-3 w-3 mr-1" />${job.hourly_rate}/hr</span>
                          <span className="flex items-center" data-testid={`owner-job-start-date-${job.id}`}><Calendar className="h-3 w-3 mr-1" />{toDateInputValue(job.start_date) || 'No start date'}</span>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 w-full lg:w-auto">
                        <Button variant="outline" size="sm" onClick={() => handleOpenEdit(job)} data-testid={`owner-edit-job-btn-${job.id}`} disabled={actionLoadingKey !== ''}>
                          <Edit3 className="h-3 w-3 mr-1" />Edit
                        </Button>
                        {isActive && (
                          <Button variant="outline" size="sm" onClick={() => handleCloseJob(job.id)} data-testid={`owner-close-job-btn-${job.id}`} disabled={actionLoadingKey === `close-${job.id}`}>
                            <XCircle className="h-3 w-3 mr-1" />Close
                          </Button>
                        )}
                        {!isActive && canUndoClose(job) && (
                          <Button variant="outline" size="sm" onClick={() => handleReopenJob(job.id)} data-testid={`owner-reopen-job-btn-${job.id}`} disabled={actionLoadingKey === `reopen-${job.id}`}>
                            <RotateCcw className="h-3 w-3 mr-1" />Undo Close
                          </Button>
                        )}
                        {isActive && (
                          <Button size="sm" className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-light))]" onClick={() => handleInviteBartenders(job.id)} data-testid={`owner-invite-bartenders-btn-${job.id}`} disabled={actionLoadingKey === `invite-${job.id}`}>
                            <UserPlus className="h-3 w-3 mr-1" />Invite Bartenders
                          </Button>
                        )}
                        <Button variant="destructive" size="sm" onClick={() => handleDeleteJob(job.id)} data-testid={`owner-delete-job-btn-${job.id}`} disabled={actionLoadingKey === `delete-${job.id}`}>
                          <Trash2 className="h-3 w-3 mr-1" />Delete
                        </Button>
                        <Button variant="outline" size="sm" className="col-span-2 sm:col-auto" onClick={() => setExpandedJobId(isExpanded ? null : job.id)} data-testid={`owner-view-applicants-btn-${job.id}`}>
                          <Users className="h-3 w-3 mr-1" />
                          Applicants
                          {isExpanded ? <ChevronUp className="h-3 w-3 ml-1" /> : <ChevronDown className="h-3 w-3 ml-1" />}
                        </Button>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="mt-4 pt-4 border-t border-white/10" data-testid={`owner-applicants-panel-${job.id}`}>
                        <div className="flex flex-wrap gap-2 mb-3" data-testid={`owner-applicant-filters-${job.id}`}>
                          <Button
                            variant={selectedApplicantFilter === 'pending' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setApplicantFilter(job.id, 'pending')}
                            data-testid={`owner-applicant-filter-pending-${job.id}`}
                          >
                            Pending
                          </Button>
                          <Button
                            variant={selectedApplicantFilter === 'accepted' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setApplicantFilter(job.id, 'accepted')}
                            data-testid={`owner-applicant-filter-accepted-${job.id}`}
                          >
                            Accepted
                          </Button>
                          <Button
                            variant={selectedApplicantFilter === 'rejected' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setApplicantFilter(job.id, 'rejected')}
                            data-testid={`owner-applicant-filter-rejected-${job.id}`}
                          >
                            Rejected
                          </Button>
                        </div>

                        {jobApplications.length === 0 ? (
                          <p className="text-sm text-muted-foreground" data-testid={`owner-no-applicants-${job.id}`}>No applicants yet for this job.</p>
                        ) : filteredApplications.length === 0 ? (
                          <p className="text-sm text-muted-foreground" data-testid={`owner-no-applicants-for-filter-${job.id}`}>
                            No {selectedApplicantFilter} applicants for this job.
                          </p>
                        ) : (
                          <div className="space-y-3">
                            {filteredApplications.map((application) => {
                              const isPending = application.status === 'pending';
                              const bartenderName = `${application?.bartender?.first_name || 'Applicant'} ${application?.bartender?.last_name || ''}`.trim();

                              return (
                                <div key={application.id} className="rounded-md border border-white/10 p-3" data-testid={`owner-applicant-row-${application.id}`}>
                                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                                    <div>
                                      <p className="font-medium text-white" data-testid={`owner-applicant-name-${application.id}`}>{bartenderName}</p>
                                      <p className="text-xs text-muted-foreground" data-testid={`owner-applicant-meta-${application.id}`}>
                                        Match: {application.match_score ? `${Math.round(application.match_score * 100)}%` : 'N/A'} • Applied: {toDateInputValue(application.created_at) || 'Today'}
                                      </p>
                                    </div>

                                    <div className="flex flex-wrap gap-2 items-center">
                                      <Badge
                                        className={
                                          application.status === 'accepted'
                                            ? 'bg-green-500/20 text-green-400 border-green-500/30'
                                            : application.status === 'rejected'
                                            ? 'bg-red-500/20 text-red-400 border-red-500/30'
                                            : 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
                                        }
                                        data-testid={`owner-applicant-status-badge-${application.id}`}
                                      >
                                        {application.status}
                                      </Badge>

                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleMessageApplicant(application)}
                                        data-testid={`owner-message-applicant-btn-${application.id}`}
                                        disabled={actionLoadingKey === `message-${application.id}`}
                                      >
                                        <MessageSquare className="h-3 w-3 mr-1" />Message
                                      </Button>

                                      <Button
                                        size="sm"
                                        onClick={() => handleApplicationStatus(application.id, 'accepted')}
                                        data-testid={`owner-accept-applicant-btn-${application.id}`}
                                        disabled={!isPending || actionLoadingKey === `accepted-${application.id}`}
                                      >
                                        Accept
                                      </Button>

                                      <Button
                                        variant="destructive"
                                        size="sm"
                                        onClick={() => handleApplicationStatus(application.id, 'rejected')}
                                        data-testid={`owner-reject-applicant-btn-${application.id}`}
                                        disabled={!isPending || actionLoadingKey === `rejected-${application.id}`}
                                      >
                                        Reject
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-xl" data-testid="owner-edit-job-dialog">
          <DialogHeader>
            <DialogTitle>Edit Job</DialogTitle>
            <DialogDescription>Update your job details without leaving the dashboard.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <Input
              value={editForm.title}
              onChange={(event) => setEditForm((prev) => ({ ...prev, title: event.target.value }))}
              placeholder="Job title"
              data-testid="owner-edit-job-title-input"
            />
            <Input
              type="number"
              min="10"
              value={editForm.hourly_rate}
              onChange={(event) => setEditForm((prev) => ({ ...prev, hourly_rate: event.target.value }))}
              placeholder="Hourly rate"
              data-testid="owner-edit-job-rate-input"
            />
            <Input
              type="date"
              value={editForm.start_date}
              onChange={(event) => setEditForm((prev) => ({ ...prev, start_date: event.target.value }))}
              data-testid="owner-edit-job-start-date-input"
            />
            <Textarea
              rows={4}
              value={editForm.description}
              onChange={(event) => setEditForm((prev) => ({ ...prev, description: event.target.value }))}
              placeholder="Job description"
              data-testid="owner-edit-job-description-input"
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)} data-testid="owner-edit-job-cancel-btn">Cancel</Button>
            <Button onClick={handleUpdateJob} disabled={actionLoadingKey === `edit-${editingJob?.id}`} data-testid="owner-edit-job-save-btn">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
